import styled, { DefaultTheme } from 'styled-components';
import { Card, Typography, Collapse } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

export const Container = styled.div`
  padding: 16px;
  background: ${(props) => getThemeValue(props, '#141414', '#f5f5f5')};
  transition: background 0.3s ease;
  min-height: 100vh;

  @media (min-width: 768px) {
    padding: 24px;
  }
`;

export const Title = styled(Typography.Title)`
  margin-bottom: 24px !important;
  color: ${(props) => getThemeValue(props, '#ffffff', '#1a1a1a')} !important;
  font-weight: 700 !important;
  font-size: 28px !important;

  @media (min-width: 768px) {
    font-size: 32px !important;
  }
`;

export const ReportsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const ReportCard = styled(Card)<{ $isSelected?: boolean }>`
  border-radius: 16px;
  border: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e5e7eb')};
  background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  box-shadow: ${(props) =>
    getThemeValue(
      props,
      '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)',
      '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    )};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  height: 100%;
  border-color: ${(props) =>
    props.$isSelected
      ? getThemeValue(props, '#2D6CCA', '#2D6CCA')
      : getThemeValue(props, '#4a5568', '#e5e7eb')};
  background: ${(props) =>
    props.$isSelected
      ? getThemeValue(props, '#1e3a5f', '#e6f4ff')
      : getThemeValue(props, '#1a202c', '#ffffff')};

  &:hover {
    box-shadow: ${(props) =>
      getThemeValue(
        props,
        '0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.3)',
        '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
      )};
    border-color: ${(props) => getThemeValue(props, '#2D6CCA', '#2D6CCA')};
  }

  .ant-card-body {
    padding: 24px;
    background: transparent;
  }
`;

export const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

export const IconWrapper = styled.div<{ $iconColor?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${(props) =>
    props.$iconColor
      ? `${props.$iconColor}15`
      : getThemeValue(props, 'rgba(45, 108, 202, 0.15)', 'rgba(45, 108, 202, 0.1)')};
  color: ${(props) => (props.$iconColor ? props.$iconColor : '#2D6CCA')};
  flex-shrink: 0;
  font-size: 24px;
  transition: all 0.3s ease;

  @media (min-width: 768px) {
    width: 56px;
    height: 56px;
    font-size: 28px;
  }
`;

export const CardText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

export const CardTitle = styled(Typography.Title)`
  margin: 0 !important;
  color: ${(props) => getThemeValue(props, '#ffffff', '#1a1a1a')} !important;
  font-weight: 700 !important;
  font-size: 18px !important;
  line-height: 1.3 !important;

  @media (min-width: 768px) {
    font-size: 20px !important;
  }
`;

export const CardSubtitle = styled(Typography.Text)`
  color: ${(props) => getThemeValue(props, '#a0aec0', '#666666')} !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  line-height: 1.4 !important;
`;

export const ArrowIcon = styled(FontAwesomeIcon)`
  color: ${(props) => getThemeValue(props, '#a0aec0', '#8c8c8c')};
  font-size: 16px;
  transition: transform 0.3s ease;
  
  ${ReportCard}:hover & {
    color: #2D6CCA;
  }
`;

export const StyledCollapse = styled(Collapse)`
  margin-top: 16px;
  margin-bottom: 16px;
  background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  border: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e5e7eb')};
  border-radius: 12px;
  width: 100%;
  grid-column: 1 / -1;

  .ant-collapse-item {
    border: none;
  }

  .ant-collapse-header {
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
    color: ${(props) => getThemeValue(props, '#ffffff', '#1a1a1a')} !important;
    padding: 16px 20px !important;
    border-radius: 12px !important;
  }

  .ant-collapse-content {
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
    border-top: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e5e7eb')};
  }

  .ant-collapse-content-box {
    padding: 20px !important;
  }
`;

export const DetailCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e5e7eb')};
  background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  box-shadow: ${(props) =>
    getThemeValue(
      props,
      '0 2px 4px rgba(0, 0, 0, 0.2)',
      '0 1px 3px rgba(0, 0, 0, 0.08)'
    )};

  .ant-card-body {
    padding: 20px;
  }
`;

export const DetailTitle = styled(Typography.Title)`
  margin-bottom: 12px !important;
  color: ${(props) => getThemeValue(props, '#ffffff', '#1a1a1a')} !important;
  font-weight: 600 !important;
  font-size: 18px !important;
`;

export const DetailDescription = styled(Typography.Text)`
  color: ${(props) => getThemeValue(props, '#a0aec0', '#666666')} !important;
  font-size: 14px !important;
  line-height: 1.6 !important;
`;
